import React from 'react';
import { Link } from 'react-router-dom';
import { PRODUCT_CATEGORIES } from '../data/ProductCategories';

interface CategoryItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  backgroundImage?: string;
}

interface ProductCategoriesSectionProps {
  title?: string;
  subtitle?: string;
  categories?: CategoryItem[];
}

const ProductCategoriesSection: React.FC<ProductCategoriesSectionProps> = ({
  title = "Product Categories",
  subtitle = "Products are available through PES in Canada. Browse core categories, then contact us for pricing and current availability.",
  categories
}) => {
  // Default categories if none provided
  const defaultCategories: CategoryItem[] = [
    {
      title: PRODUCT_CATEGORIES.AUTOMOTIVE_LUBRICANTS,
      description: "Gasoline, diesel, motorcycle, and transmission products for vehicle and fleet applications.",
      icon: (
        <svg
          className="w-10 h-10 text-red-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          ></path>
        </svg>
      ),
      category: PRODUCT_CATEGORIES.AUTOMOTIVE_LUBRICANTS,
      backgroundImage: "/images/product-categories/automotive.png"
    },
    {
      title: PRODUCT_CATEGORIES.INDUSTRIAL_LUBRICANTS,
      description: "Hydraulic, gear, compressor, metalworking, slideway, and synthetic industrial products.",
      icon: (
        <svg
          className="w-10 h-10 text-red-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
          ></path>
        </svg>
      ),
      category: PRODUCT_CATEGORIES.INDUSTRIAL_LUBRICANTS,
      backgroundImage: "/images/product-categories/industry.png"
    },
    {
      title: PRODUCT_CATEGORIES.MARINE_LUBRICANTS,
      description: "Marine engine oils and related products for vessel and marine equipment applications.",
      icon: (
        <svg
          className="w-10 h-10 text-red-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 10V3L4 14h7v7l9-11h-7z"
          ></path>
        </svg>
      ),
      category: PRODUCT_CATEGORIES.MARINE_LUBRICANTS,
      backgroundImage: "/images/product-categories/marine.png"
    },
    {
      title: PRODUCT_CATEGORIES.GREASES,
      description: "Greases for automotive, mining, bearing, gear, extreme pressure, and high-temperature uses.",
      icon: (
        <svg
          className="w-10 h-10 text-red-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 10V3L4 14h7v7l9-11h-7z"
          ></path>
        </svg>
      ),
      category: PRODUCT_CATEGORIES.GREASES,
      backgroundImage: "/images/product-categories/grease.png"
    },
    {
      title: PRODUCT_CATEGORIES.SPECIALTY_FLUIDS,
      description: "Brake fluids, exhaust fluids, fire-proof hydraulic fluid, and other fluid products.",
      icon: (
        <svg
          className="w-10 h-10 text-red-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
          ></path>
        </svg>
      ),
      category: PRODUCT_CATEGORIES.SPECIALTY_FLUIDS,
      backgroundImage: "/images/product-categories/specialty fluid.png"
    }
  ];

  const displayCategories = categories || defaultCategories;

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">{title}</h2>
          <div className="w-20 h-1 bg-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {displayCategories.map((item, index) => (
            <div
              key={index}
              className="relative overflow-hidden bg-white p-6 rounded-lg shadow-md text-center min-h-[280px] flex flex-col justify-end"
              style={item.backgroundImage ? { backgroundImage: `url('${item.backgroundImage}')`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
            >
              {item.backgroundImage && <div className="absolute inset-0 bg-black opacity-45"></div>}
              <div className="relative z-10">
                {!item.backgroundImage && (
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {item.icon}
                  </div>
                )}
                <h3 className={`text-xl font-semibold mb-2 ${item.backgroundImage ? "text-white" : "text-gray-900"}`}>{item.title}</h3>
                <p className={`mb-4 ${item.backgroundImage ? "text-gray-100" : "text-gray-600"}`}>{item.description}</p>
                <Link
                  to="/products"
                  className={`font-medium inline-flex items-center ${item.backgroundImage ? "text-white hover:text-red-100" : "text-red-600 hover:text-red-700"}`}
                  onClick={() => window.localStorage.setItem("productCategory", item.category)}
                >
                  View Products
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    ></path>
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductCategoriesSection; 
